import { EditorPanelItem } from '@editor/interfaces/editor'
import { PayloadAction, createSlice } from '@reduxjs/toolkit'
import { merge, uniq } from 'lodash'

interface InspectionGroupState {
  /**
   * All inspection groups in the area.
   */
  groups: EditorPanelItem[]

  /**
   * Key of the currently dragged item.
   */
  draggingItemKey?: string

  /**
   * Keys of the groups that have been edited but not yet saved.
   */
  editedGroupKeys: string[]
}

const initialState: InspectionGroupState = {
  groups: [],
  editedGroupKeys: [],
}

const slice = createSlice({
  name: 'inspectionGroup',
  initialState,
  reducers: {
    setInspectionGroups: (state: InspectionGroupState, action: PayloadAction<EditorPanelItem[]>) => {
      state.groups = action.payload
    },
    addInspectionGroup: (state: InspectionGroupState, action: PayloadAction<EditorPanelItem>) => {
      state.groups.push(action.payload)
    },
    removeInspectionGroup: (state: InspectionGroupState, action: PayloadAction<string>) => {
      state.groups = state.groups.filter((group) => group.key !== action.payload)
    },

    /**
     * Updating the inspection group here will save the data to the server as well.
     */
    patchInspectionGroup: (
      state: InspectionGroupState,
      { payload }: PayloadAction<Omit<Partial<EditorPanelItem>, 'key'> & Pick<EditorPanelItem, 'key'>>,
    ) => {
      const index = state.groups.findIndex((group) => group.key === payload.key)
      if (index !== -1) {
        state.groups[index] = merge({}, state.groups[index], payload)

        // Don't trigger saving if it's a new item
        if (!state.groups[index].isWorking) {
          state.editedGroupKeys = uniq([...state.editedGroupKeys, payload.key])
        }
      }
    },

    /**
     * Updating the inspection group here will only update the local state.
     */
    updateInspectionGroup: (
      state: InspectionGroupState,
      { payload }: PayloadAction<Omit<Partial<EditorPanelItem>, 'key'> & Pick<EditorPanelItem, 'key'>>,
    ) => {
      const index = state.groups.findIndex((group) => group.key === payload.key)
      if (index !== -1) {
        state.groups[index] = merge({}, state.groups[index], payload)
      }
    },

    /**
     * Batch update multiple inspection groups at once.
     */
    updateInspectionGroups: (
      state: InspectionGroupState,
      { payload }: PayloadAction<(Omit<Partial<EditorPanelItem>, 'key'> & Pick<EditorPanelItem, 'key'>)[]>,
    ) => {
      payload.forEach((update) => {
        const index = state.groups.findIndex((group) => group.key === update.key)
        if (index !== -1) {
          state.groups[index] = merge({}, state.groups[index], update)
        }
      })
    },

    reorderInspectionGroups: (state: InspectionGroupState, { payload }: PayloadAction<string[]>) => {
      // Get the groups we want to reorder and the rest of the groups
      const newGroups = payload.map((key) => state.groups.find((group) => group.key === key))
      const others = state.groups.filter((group) => !payload.includes(group.key))

      // Combine them both so that ones we want to reorder are ordered as requested and
      // the rest comes afterwards.
      state.groups = newGroups
        .concat(others)
        .filter((group): group is EditorPanelItem => group !== undefined)
        .map((group, index) => ({
          ...group,
          order: index,
        }))
      state.editedGroupKeys = uniq([...state.editedGroupKeys, ...payload])
    },

    /**
     *
     * @param state InspectionGroupState
     * @param payload The payload contains the key of the group to update and the new order.
     */
    updateNewOrder: (state: InspectionGroupState, { payload }: PayloadAction<{ key: string; order: number }>) => {
      // Sort by current order to ensure consistency
      const sorted = [...state.groups].sort((a, b) => (a.order || 0) - (b.order || 0))

      // Remove the item to move
      const targetIndex = sorted.findIndex((item) => item.key === payload.key)
      if (targetIndex === -1) return

      const [movingItem] = sorted.splice(targetIndex, 1)

      // Insert at new position (adjusting for 1-based order)
      sorted.splice(payload.order - 1, 0, movingItem)

      state.groups = sorted.map((item, index) => ({
        ...item,
        order: index + 1, // Ensure order is 1-based
      }))
    },
    replaceSavedInspectionGroup: (
      state: InspectionGroupState,
      action: PayloadAction<{ oldKey: string; item: EditorPanelItem }>,
    ) => {
      const index = state.groups.findIndex((group) => group.key === action.payload.oldKey)
      if (index !== -1) {
        state.groups[index] = action.payload.item
      }
    },

    setDraggingItem: (state: InspectionGroupState, action: PayloadAction<string | undefined>) => {
      state.draggingItemKey = action.payload
    },

    setEditedGroupKeys: (state: InspectionGroupState, { payload }: PayloadAction<string[]>) => {
      state.editedGroupKeys = payload
    },

    resetSavingStates: (state: InspectionGroupState) => {
      state.groups = state.groups.map((group) => ({
        ...group,
        isSaving: false,
      }))
    },
  },
})

export const {
  setInspectionGroups,
  addInspectionGroup,
  removeInspectionGroup,
  patchInspectionGroup,
  updateInspectionGroup,
  updateInspectionGroups,
  updateNewOrder,
  reorderInspectionGroups,
  replaceSavedInspectionGroup,

  setDraggingItem,

  setEditedGroupKeys,

  resetSavingStates,
} = slice.actions

export default slice.reducer
